import React from "react";
import { FlaskConical, RefreshCw, MapPin } from "lucide-react";
import {
  fetchDemoMode,
  setDemoModeEnabled,
  refreshDemoModeData,
  type DemoModeResponse
} from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

/**
 * Demo Mode — DEV ONLY.
 *
 * One switch, for the minutes before a demo. Turning it on tells the demo runner
 * on the dev box to age the whole seeded world forward so it reads as today, and
 * to start driving the field reps around the live map. Turning it off parks them.
 *
 * There is no production equivalent: the API 404s when ORBIT_ENV=prod, so this
 * page reports itself unavailable and the sidebar never offers it.
 */
const POLL_MS = 8000;

export function DemoModePage() {
  usePageTitle("Demo mode");
  const [data, setData] = React.useState<DemoModeResponse | null>(null);
  const [unavailable, setUnavailable] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<null | "toggle" | "refresh">(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async (quiet?: boolean) => {
    if (!quiet) setLoading(true);
    try {
      setData(await fetchDemoMode());
      setUnavailable(false);
      setError(null);
    } catch (e) {
      // A 404 is the deliberate answer on production, not a fault.
      const msg = e instanceof Error ? e.message : "";
      if (/404|not found/i.test(msg)) setUnavailable(true);
      else if (!quiet) setError(msg || "Could not load demo mode");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    const id = setInterval(() => { void load(true); }, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const toggle = async () => {
    if (!data) return;
    const next = !data.state.enabled;
    setBusy("toggle"); setError(null);
    try {
      await setDemoModeEnabled(next);
      await load(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not change demo mode");
    } finally { setBusy(null); }
  };

  const refresh = async () => {
    setBusy("refresh"); setError(null);
    try {
      await refreshDemoModeData();
      await load(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not request a refresh");
    } finally { setBusy(null); }
  };

  if (unavailable) {
    return (
      <div className="page">
        <header className="page-header">
          <span className="eyebrow">Operations</span>
          <h1>Demo mode</h1>
        </header>
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            Demo mode does not exist in this environment. It only ever runs against the
            development database, and there is no production equivalent by design.
          </p>
        </div>
      </div>
    );
  }

  const s = data?.state;
  const on = !!s?.enabled;
  const alive = !!data?.runnerAlive;
  // "Working" is the honest state between the click and the runner finishing:
  // the shift takes a few seconds and the map is not live until it is done.
  const working = on && (s?.runnerState === "starting" || (!!s && s.refreshToken > s.appliedToken));
  const live = on && alive && s?.runnerState === "running";

  return (
    <div className="page">
      <header
        className="page-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}
      >
        <div>
          <span className="eyebrow">Operations</span>
          <h1>Demo mode</h1>
          <p className="muted">
            Flip this on before a demo. It moves every date in the{" "}
            <strong>{data?.tenantName || "demo"}</strong> dev tenant forward so the data reads as
            today, then puts the field reps on the live map and keeps them driving. Off parks them.
          </p>
        </div>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <button
            className={on ? "btn btn-ghost" : "btn"}
            disabled={busy !== null || loading}
            onClick={toggle}
          >
            <FlaskConical size={16} className={working ? "spin" : undefined} />{" "}
            {busy === "toggle" ? "…" : on ? "Stop demoing" : "I'm demoing"}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            title="Re-date the data to today without switching off"
            disabled={!on || busy !== null}
            onClick={refresh}
          >
            <RefreshCw size={14} className={busy === "refresh" ? "spin" : undefined} /> Re-date now
          </button>
        </div>
      </header>

      {error ? <div style={{ color: "var(--danger, #c0392b)", marginBottom: 12 }}>{error}</div> : null}
      {loading && !data ? <p className="muted">Loading…</p> : null}

      {data ? (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="row between" style={{ alignItems: "center", gap: 16 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 18 }}>
                  {live ? "Live" : working ? "Starting up…" : on ? "On, but the runner is not answering" : "Off"}{" "}
                  {live ? <span className="pill">demoing</span> : null}
                  {on && !alive && !working ? <span className="pill warn">runner offline</span> : null}
                  {s?.runnerState === "error" ? <span className="pill warn">error</span> : null}
                </div>
                <div className="muted" style={{ marginTop: 4 }}>
                  {s?.runnerMessage
                    || (on ? "Waiting for the runner to pick this up." : "Nothing is being changed or driven.")}
                </div>
                <div className="faint" style={{ marginTop: 4 }}>
                  Data reads as <code>{s?.dataEpoch || "unknown"}</code>
                  {s?.lastShiftAt
                    ? ` • last moved ${s.lastShiftDays ?? 0} day(s) forward at ${new Date(s.lastShiftAt).toLocaleString()}`
                    : ""}
                  {s?.updatedBy ? ` • switched by ${s.updatedBy}` : ""}
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="section-head"><h2>Reps on the map</h2></div>
            {data.reps.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                Nobody has a position yet. Switch demo mode on and give it a few seconds.
              </p>
            ) : (
              <div className="col" style={{ gap: 10 }}>
                {data.reps.map((r) => (
                  <div className="row between" key={r.name} style={{ alignItems: "center" }}>
                    <div className="row" style={{ gap: 8, alignItems: "center" }}>
                      <MapPin size={14} style={{ color: r.ageSeconds < 60 ? "var(--success)" : "var(--muted)" }} />
                      <span>{r.name}</span>
                    </div>
                    <div className="row" style={{ gap: 10, alignItems: "center" }}>
                      <span className="pill">{r.moving ? "driving" : "stopped"}</span>
                      <span className={r.ageSeconds < 60 ? "muted" : "faint"}>
                        {r.ageSeconds < 60 ? `${r.ageSeconds}s ago` : `${Math.round(r.ageSeconds / 60)} min ago`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="section-head"><h2>How current the data looks</h2></div>
            <div className="col" style={{ gap: 8 }}>
              <div className="row between">
                <span className="muted">Newest invoice</span>
                <span>{data.data.newestInvoiceDate || "—"}</span>
              </div>
              <div className="row between">
                <span className="muted">Most recent won deal</span>
                <span>{data.data.newestWonDealDate || "—"}</span>
              </div>
              <div className="row between">
                <span className="muted">Open deals in the pipeline</span>
                <span>{data.data.openDeals}</span>
              </div>
            </div>
            <p className="faint" style={{ marginTop: 12, marginBottom: 0 }}>
              Switching on shifts every dated row in this tenant by the gap between the epoch above
              and today, so relative timing holds: an invoice that was forty days overdue stays
              forty days overdue. Sales targets and the spread of won deals are recomputed rather
              than shifted, because they hang off month boundaries.
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}
